import React from 'react';
import {
  Box, Grid, Typography, Link, Paper
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const cardVariants = {
  hover: {
    y: -10,
    scale: 1.03,
    boxShadow: '0 25px 40px rgba(0,0,0,0.2)',
    transition: { duration: 0.4 }
  }
};

const iconStyle = {
  fontSize: { xs: 32, sm: 38 },
  color: '#1976d2',
};

export default function ContactSection() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const fontFamily = isRTL ? "'Tajawal', sans-serif" : 'inherit';

  // helper: reverse number visually when RTL
  const formatPhoneNumber = (number) => {
    if (isRTL) {
      return number.split('').reverse().join('');
    }
    return number;
  };

  const contactItems = [
    {
      icon: <LocationOnIcon sx={iconStyle} />,
      label: t('address'),
      content: t('addressContent'),
    },
    {
      icon: <EmailIcon sx={iconStyle} />,
      label: t('email'),
      content: (
        <Link
          href={`mailto:${t('emailAddress')}`}
          underline="hover"
          color="primary"
          aria-label={t('email')}
          sx={{
            direction: i18n.dir(),
            unicodeBidi: 'bidi-override',
            display: 'inline-block',
          }}
        >
          {t('emailAddress')}
        </Link>
      ),
    },
    {
      icon: <PhoneIcon sx={iconStyle} />,
      label: t('phone'),
      content: (
        <Box
          component="span"
          sx={{
            direction: i18n.dir(),         // rtl or ltr
            unicodeBidi: 'bidi-override',  // enforce bidi rules
            display: 'inline-block',
          }}
        >
          {formatPhoneNumber(t('phoneNumbers'))}
        </Box>
      ),
    }
  ];

  return (
    <Box
      sx={{
        bgcolor: 'lightgray',
        py: 10,
        px: { xs: 2, sm: 6, md: 10 },
        mx: { xs: 1, md: 6 },
        my: 8,
        borderRadius: 8,
        backdropFilter: 'blur(8px)',
        boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
        border: '1px solid rgba(255,255,255,0.2)',
        direction: i18n.dir(),  // RTL/LTR support
        textAlign: i18n.dir() === 'rtl' ? 'right' : 'left',
      }}
    >
      <Typography
        fontFamily={fontFamily}
        variant="h3"
        align="center"
        sx={{
          mb: 8,
          fontWeight: 700,
          color: '#1976d2',
          textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
        }}
      >
        {t('contactUs')}
      </Typography>

      <Grid container spacing={6}>
        {/* Contact Info */}
        <Grid item xs={12} md={6}>
          <Grid container spacing={4}>
            {contactItems.map(({ icon, label, content }, index) => (
              <Grid item xs={12} key={index}>
                <motion.div whileHover="hover" variants={cardVariants}>
                  <Paper
                    elevation={4}
                    sx={{
                      p: 4,
                      background: 'linear-gradient(145deg, #ffffff, #e6e6e6)',
                      borderRadius: 6,
                      textAlign: i18n.dir() === 'rtl' ? 'right' : 'left',
                      boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.5), 0 4px 12px rgba(0,0,0,0.1)',
                      transition: '0.4s',
                      whiteSpace: 'pre-line',
                      fontSize: 15,
                    }}
                  >
                    {icon}
                    <Typography fontFamily={fontFamily} variant="h6" sx={{ mt: 2 }}>
                      {label}
                    </Typography>
                    <Typography fontFamily={fontFamily} sx={{ mt: 1 }}>
                      {content}
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Google Map */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Box
              sx={{
                borderRadius: 6,
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                minHeight: { xs: 300, sm: 420 }
              }}
            >
              <a
                href="https://www.google.com/maps/place/24%C2%B039'01.9%22N+46%C2%B042'23.7%22E/@24.6505184,46.7040099,17z"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="image/Map.png"
                  alt={t('googleMapAlt')}
                  style={{
                    width: '100%',
                    height: '670px',
                    objectFit: 'cover',
                    cursor: 'pointer',
                    transition: '0.3s',
                    transform: 'scale(1)',
                  }}
                />
              </a>
            </Box>
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  );
}
